<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <div class="custom-toolbar md-layout">
                    <div class="md-layout-item custom-toolbar-start md-layout md-small-size-80 md-size-90">
                        <team-members-filter :default-value="initialTeamMember" @onSelection="onTeamMemberSelection" />

                        <div class="md-layout-item">
                            <date-range-filter
                                :is-single-date="true"
                                :default-date="initialDate"
                                @onChanged="onDateChanged"
                            />
                        </div>
                    </div>
                    <div class="md-layout-item custom-toolbar-end">
                        <md-button
                            title="Export Stops report"
                            class="md-primary md-just-icon md-round pull-right header-button"
                            @click="onExportToExcel"
                        >
                            <md-icon>get_app</md-icon>
                        </md-button>
                    </div>
                </div>
            </div>
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-blue">
                        <div class="card-icon">
                            <md-icon>summarize</md-icon>
                        </div>
                    </md-card-header>

                    <md-card-content>
                        <div v-if="!reports || reports.length == 0">
                            <h3>Stop Report</h3>
                            <p>
                                No results to display.
                            </p>
                        </div>
                        <div v-else>
                            <div class="report-container">
                                <md-table :value="reports" class="table-hover">
                                    <md-table-row slot="md-table-row" slot-scope="{ item, index }">
                                        <md-table-cell>
                                            {{ index + 1 }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Type" md-sort>
                                            {{ item.tripState }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Date/Time">
                                            {{ item.startTime | timeFormat(DATE_TYPES.standardTime) }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Suburb">
                                            {{ item.startSuburb }} - {{ item.endSuburb }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Distance">
                                            {{ item.totalDistanceKm }} kms
                                        </md-table-cell>
                                        <md-table-cell md-label="Duration">{{ item.durationMin }} mins</md-table-cell>
                                    </md-table-row>
                                </md-table>
                            </div>
                        </div>
                    </md-card-content>
                </md-card>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { mapMutations, mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { TeamMembersFilter, DateRangeFilter } from '../components';

export default {
    name: 'GpsStops',
    components: { TeamMembersFilter, DateRangeFilter },
    mixins: [GeneralMixin],
    data() {
        const savedArgs = localStorage.getItem('gps-reports.search-args');
        const parsedArgs = savedArgs ? JSON.parse(savedArgs) : null;

        return {
            localStorageKey: 'gps-reports.search-args',
            searchArgs: {
                eventSourceId: parsedArgs?.eventSourceId || null,
                eventDate: parsedArgs?.eventDate || null,
                name: parsedArgs?.name || '',
                isTracker: parsedArgs?.isTracker || false,
                teamRegionId: parsedArgs?.teamRegionId !== undefined ? parsedArgs.teamRegionId : null
            },
            initialTeamMember: parsedArgs
                ? {
                    eventSourceId: parsedArgs.eventSourceId,
                    isTracker: parsedArgs.isTracker || false,
                    teamRegionId: parsedArgs.teamRegionId !== undefined ? parsedArgs.teamRegionId : null
                }
                : null,
            initialDate: parsedArgs?.eventDate || null,
            reports: []
        };
    },
    methods: {
        ...mapGetters({
            user: 'user/user'
        }),
        ...mapMutations({
            changeDate: 'map/CHANGE_DATE'
        }),

        async onExportToExcel() {
            if (!this.searchArgs.eventSourceId || !this.searchArgs.eventDate) 
                return;

            try {
                const { eventSourceId, eventDate, isTracker } = this.searchArgs;
                const eventDateStr = moment(eventDate).format('YYYY-MM-DD');

                const api = `/api/gps-reports/${eventSourceId}/stops/export-to-excel?eventDate=${eventDateStr}&isTracker=${isTracker}`;
                const payload = {
                    method: 'get',
                    responseType: 'blob'
                };

                this.$_handleLoaderState(true);

                const { data } = await handleRequests(api, payload);
                const reportName = `${this.searchArgs.name}-stops-${eventDateStr}.xlsx`;
                downloadFile(data, reportName);
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                showErrorMessage(this, 'Error exporting to Excel.', error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async getReports() {
            if (!this.searchArgs.eventSourceId || !this.searchArgs.eventDate) 
                return;
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.searchArgs));

            try {
                const { eventSourceId, eventDate, isTracker } = this.searchArgs;
                const eventDateStr = moment(eventDate).format('YYYY-MM-DD');

                const api = `/api/gps-reports/${eventSourceId}/stops?eventDate=${eventDateStr}&isTracker=${isTracker}`;

                this.$_handleLoaderState(true);

                const { data } = await handleRequests(api);
                this.reports = data || [];
            } catch (error) {
                console.error('Error fetching reports:', error);
                showErrorMessage(this, 'Failed to fetch reports.', error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async onDateChanged(val) {
            if (!val) {
                this.searchArgs.eventDate = null;
                return;
            }

            this.searchArgs.eventDate = val;
            this.getReports();
        },

        async onTeamMemberSelection(val) {
            // Always save teamRegionId when it's provided
            if (val?.teamRegionId !== undefined) {
                this.searchArgs.teamRegionId = val.teamRegionId;
            }

            if (!val?.userId) {
                this.searchArgs.eventSourceId = null;
                this.searchArgs.name = '';
                return;
            }

            this.searchArgs.eventSourceId = val.userId; // userId is either publicUserId or trackerReference
            this.searchArgs.name = val.name || '';
            this.searchArgs.isTracker = val.isTracker || false;
            this.getReports();
        }
    }
};
</script>
